import React from 'react';
import { ago } from '../dash/palette';
import { ChatSession, displayName } from './types';

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--oa-muted)]">{children}</p>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <Heading>{label}</Heading>
    <div className="mt-0.5 text-[13px] text-[color:var(--oa-text)]">{children}</div>
  </div>
);

const renderProductContext = (context: any) => {
  if (context == null) return null;
  if (typeof context === 'string') return <p className="text-[12.5px] text-[color:var(--oa-text)]">{context}</p>;
  if (typeof context === 'object') {
    const entries = Object.entries(context).filter(([, v]) => v !== null && v !== undefined && v !== '');
    if (!entries.length) return null;
    return (
      <div className="space-y-1">
        {entries.map(([key, value]) => (
          <div key={key} className="flex justify-between gap-3 text-[12.5px]">
            <span className="capitalize text-[color:var(--oa-muted)]">{key.replace(/_/g, ' ')}</span>
            <span className="truncate text-right text-[color:var(--oa-text)]">{String(value)}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const VisitorPanel: React.FC<{ session: ChatSession }> = ({ session }) => {
  const productContext = renderProductContext(session.product_context);
  const started = new Date(session.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto p-4">
      <Row label="Visitor">{displayName(session)}</Row>

      {session.visitor_email ? (
        <Row label="Email">
          <a href={`mailto:${session.visitor_email}`} className="text-[color:var(--oa-s3)] hover:underline">
            {session.visitor_email}
          </a>
        </Row>
      ) : null}

      <Row label="Status">{session.status === 'waiting_for_admin' ? 'Waiting' : session.status === 'admin_joined' ? 'With team' : 'AI assistant'}</Row>
      <Row label="Started">{started}</Row>
      <Row label="Last activity">{session.last_message_at ? ago(session.last_message_at) : '—'}</Row>

      {session.current_page ? (
        <Row label="Current page">
          <a href={session.current_page} target="_blank" rel="noopener noreferrer" className="block truncate font-mono text-[11px] text-[color:var(--oa-s3)] hover:underline">
            {session.current_page}
          </a>
        </Row>
      ) : null}

      {session.admin_name ? <Row label="Handled by">{session.admin_name}</Row> : null}

      {productContext ? <Row label="Product context">{productContext}</Row> : null}
    </div>
  );
};
